import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { 
    Users, Shield, Activity, DollarSign, MapPin, 
    FileText, Check, AlertTriangle, ArrowRight, ShieldAlert,
    TrendingUp, Award, Calendar, ExternalLink, Download, Printer
} from "lucide-react";

export default function AdminStudio() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("users");
    const [loading, setLoading] = useState(true);
    const [tenants, setTenants] = useState([]);
    const [trafficData, setTrafficData] = useState({ active_now: 0, logs: [] });
    const [reportsData, setReportsData] = useState({
        metrics: { active_trials: 0, expired_trials: 0, expired_no_signup: 0, total_revenue: 0, total_gst: 0 },
        funnel: [],
        payments: []
    });
    const [securityData, setSecurityData] = useState([]);
    const [locationData, setLocationData] = useState([]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            // Fetch Tenants
            const tRes = await api.get("/admin-studio/tenants");
            setTenants(tRes.data);

            // Fetch Traffic
            const trRes = await api.get("/admin-studio/traffic");
            setTrafficData(trRes.data);

            // Fetch Reports
            const repRes = await api.get("/admin-studio/reports");
            setReportsData(repRes.data);

            // Fetch Security
            const secRes = await api.get("/admin-studio/security");
            setSecurityData(secRes.data);

            // Fetch Location Analytics
            const locRes = await api.get("/admin-studio/location-analytics");
            setLocationData(locRes.data);
        } catch (error) {
            console.error("Error fetching Admin Studio data:", error);
            toast.error("Failed to load global workspace data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.email === "admin@parshwebcraft.in") {
            fetchAllData();
        }
    }, [user]);

    // Check authorization
    if (user?.email !== "admin@parshwebcraft.in") {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4 border border-red-500/20">
                    <ShieldAlert className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-slate-950 dark:text-white mb-2">Access Denied</h1>
                <p className="text-slate-600 dark:text-slate-400 max-w-md">
                    The Admin Studio is restricted strictly to the global platform owner/admin.
                </p>
            </div>
        );
    }

    const handleUpdatePlan = async (tenantId, newPlan, status) => {
        try {
            await api.post(`/admin-studio/tenants/${tenantId}/plan`, {
                plan: newPlan,
                subscription_status: status
            });
            toast.success(`Plan upgraded to ${newPlan.toUpperCase()} successfully!`);
            fetchAllData();
        } catch (error) {
            toast.error("Failed to update plan.");
        }
    };

    const printGSTReport = () => {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
            <html>
            <head>
                <title>GST Billings Report - ParshHR</title>
                <style>
                    body { font-family: sans-serif; padding: 20px; color: #333; }
                    h1 { color: #0f172a; margin-bottom: 5px; }
                    h3 { color: #64748b; margin-top: 0; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; }
                    th { bg-color: #f8fafc; font-weight: bold; }
                    .totals { margin-top: 30px; font-size: 1.1em; font-weight: bold; }
                </style>
            </head>
            <body>
                <h1>ParshHR - SaaS Billing GST Audit Sheet</h1>
                <h3>Generated on: ${new Date().toLocaleDateString()}</h3>
                <p>Tax Rate: 18% GST (9% CGST, 9% SGST)</p>
                <table>
                    <thead>
                        <tr>
                            <th>Invoice / Order ID</th>
                            <th>Workspace</th>
                            <th>Base Price</th>
                            <th>CGST (9%)</th>
                            <th>SGST (9%)</th>
                            <th>Total Amount</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${reportsData.payments.map(p => `
                            <tr>
                                <td>${p.id}</td>
                                <td>${p.workspace}</td>
                                <td>₹${p.base_price.toFixed(2)}</td>
                                <td>₹${p.cgst.toFixed(2)}</td>
                                <td>₹${p.sgst.toFixed(2)}</td>
                                <td>₹${p.amount.toFixed(2)}</td>
                                <td>${p.status.toUpperCase()}</td>
                            </tr>
                        `).join("")}
                    </tbody>
                </table>
                <div class="totals">
                    <div>Total Base Price: ₹${(reportsData.metrics.total_revenue - reportsData.metrics.total_gst).toFixed(2)}</div>
                    <div>Total GST Collected: ₹${reportsData.metrics.total_gst.toFixed(2)}</div>
                    <div>Total Gross Revenue: ₹${reportsData.metrics.total_revenue.toFixed(2)}</div>
                </div>
                <script>window.print();</script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 text-slate-800 dark:text-slate-100 min-h-screen">
            {/* Top Header Card */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white dark:bg-slate-900/40 backdrop-blur-md rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                            <Shield className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-semibold tracking-widest text-emerald-600 dark:text-emerald-400 uppercase">PARSHHR SaaS ENGINE</span>
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight mt-2 text-slate-900 dark:text-white">Admin Studio</h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Platform management panel for global billing, user directories, and analytics.</p>
                </div>
                <button 
                    onClick={fetchAllData}
                    disabled={loading}
                    className="px-5 py-2.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-[0.98] transition text-sm rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 shadow-sm flex items-center gap-2"
                >
                    <Activity className={`w-4 h-4 text-emerald-500 dark:text-emerald-400 ${loading ? "animate-spin" : ""}`} />
                    Refresh Dash
                </button>
            </div>

            {/* Quick Metrics Header Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-5 bg-white dark:bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm">
                    <div className="flex justify-between items-start text-slate-500 dark:text-slate-400">
                        <span className="text-xs font-semibold uppercase tracking-wider">Active Trials</span>
                        <Calendar className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white mt-2">{reportsData.metrics.active_trials}</div>
                    <p className="text-[10px] text-slate-500 mt-1">On 14-day free plans</p>
                </div>
                
                <div className="p-5 bg-white dark:bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm">
                    <div className="flex justify-between items-start text-slate-500 dark:text-slate-400">
                        <span className="text-xs font-semibold uppercase tracking-wider">Expired Trials</span>
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white mt-2">{reportsData.metrics.expired_trials}</div>
                    <p className="text-[10px] text-slate-500 mt-1">Days remaining: 0</p>
                </div>

                <div className="p-5 bg-white dark:bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm">
                    <div className="flex justify-between items-start text-slate-500 dark:text-slate-400">
                        <span className="text-xs font-semibold uppercase tracking-wider">Expired No-Signup</span>
                        <ShieldAlert className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white mt-2">{reportsData.metrics.expired_no_signup}</div>
                    <p className="text-[10px] text-slate-500 mt-1">Unconverted workspaces</p>
                </div>

                <div className="p-5 bg-white dark:bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm">
                    <div className="flex justify-between items-start text-slate-500 dark:text-slate-400">
                        <span className="text-xs font-semibold uppercase tracking-wider">Gross Platform Revenue</span>
                        <DollarSign className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                    </div>
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">₹{reportsData.metrics.total_revenue.toLocaleString()}</div>
                    <p className="text-[10px] text-slate-500 mt-1">Includes ₹{reportsData.metrics.total_gst.toLocaleString()} GST (18%)</p>
                </div>
            </div>

            {/* Tab Navigation Menu */}
            <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 max-w-fit">
                <button
                    onClick={() => setActiveTab("users")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "users" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Users Control
                </button>
                <button
                    onClick={() => setActiveTab("workspaces")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "workspaces" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Webspaces Mod
                </button>
                <button
                    onClick={() => setActiveTab("traffic")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "traffic" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Traffic Tracker
                </button>
                <button
                    onClick={() => setActiveTab("payments")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "payments" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Reports & Payments
                </button>
                <button
                    onClick={() => setActiveTab("security")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "security" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Security Audit
                </button>
                <button
                    onClick={() => setActiveTab("location")}
                    className={`px-4 py-2 text-xs font-medium rounded-xl transition ${activeTab === "location" ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                >
                    Location Analytics
                </button>
            </div>

            {/* TAB CONTENTS */}
            <div className="bg-white/60 dark:bg-slate-900/20 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 min-h-[400px]">
                
                {/* 1. USERS CONTROL TAB */}
                {activeTab === "users" && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Registered Tenant Control</h3>
                            <span className="text-xs text-slate-500 dark:text-slate-400">{tenants.length} Workspaces Registered</span>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800/80">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="p-4">Company Name</th>
                                        <th className="p-4">Owner Email</th>
                                        <th className="p-4">Plan Status</th>
                                        <th className="p-4">Active Headcount</th>
                                        <th className="p-4">Created Date</th>
                                        <th className="p-4 text-right">Quick Plan Override</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {tenants.map(t => (
                                        <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition">
                                            <td className="p-4 font-semibold text-slate-900 dark:text-white">{t.company_name}</td>
                                            <td className="p-4 text-slate-600 dark:text-slate-400">{t.owner_email}</td>
                                            <td className="p-4">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                    t.plan === "pro" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20" :
                                                    t.plan === "lite" ? "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20" :
                                                    t.plan === "plus" ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20" :
                                                    "bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-500/20"
                                                }`}>
                                                    {t.plan}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-700 dark:text-slate-300 font-semibold">{t.active_headcount} Users</td>
                                            <td className="p-4 text-slate-500 text-xs">
                                                {t.created_at ? new Date(t.created_at).toLocaleDateString() : "N/A"}
                                            </td>
                                            <td className="p-4 text-right space-x-2">
                                                <button
                                                    onClick={() => handleUpdatePlan(t.id, "lite", "active")}
                                                    className="px-2 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition text-[10px] font-bold rounded"
                                                >
                                                    Activate Lite
                                                </button>
                                                <button
                                                    onClick={() => handleUpdatePlan(t.id, "plus", "active")}
                                                    className="px-2 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition text-[10px] font-bold rounded"
                                                >
                                                    Activate Plus
                                                </button>
                                                <button
                                                    onClick={() => handleUpdatePlan(t.id, "pro", "active")}
                                                    className="px-2 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition text-[10px] font-bold rounded"
                                                >
                                                    Activate Premium
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 2. WORKSPACES MOD */}
                {activeTab === "workspaces" && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Workspace Health & Modifications</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {tenants.map(t => (
                                <div key={t.id} className="p-5 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col justify-between shadow-sm">
                                    <div>
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-bold text-slate-900 dark:text-white text-base">{t.company_name}</h4>
                                            <span className="text-[10px] text-slate-500 uppercase tracking-widest">{t.id}</span>
                                        </div>
                                        <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400 mt-3">
                                            <div><strong>Owner:</strong> {t.owner_email}</div>
                                            <div><strong>Subscription Status:</strong> {t.subscription_status.toUpperCase()}</div>
                                            <div><strong>Trial Limit Ends:</strong> {t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString() : "N/A"}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-800/80">
                                        <div className="text-xs text-slate-500">Database Context:</div>
                                        <code className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-950 rounded text-slate-700 dark:text-slate-300 font-mono">parshhr_{t.id}</code>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 3. TRAFFIC TRACKER */}
                {activeTab === "traffic" && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Live Traffic Tracker</h3>
                                <p className="text-xs text-slate-500">List of active platform sessions across all dynamic workspaces.</p>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs rounded-xl font-bold">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                                {trafficData.active_now} users active now
                            </div>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800/80">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="p-4">User Email</th>
                                        <th className="p-4">Role</th>
                                        <th className="p-4">IP Address</th>
                                        <th className="p-4">Last Activity</th>
                                        <th className="p-4">Workspace Db</th>
                                        <th className="p-4 text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {trafficData.logs.map((log, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition">
                                            <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{log.email}</td>
                                            <td className="p-4 text-xs">
                                                <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                                                    {log.role}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-600 dark:text-slate-400 text-xs font-mono">{log.ip_address}</td>
                                            <td className="p-4 text-slate-600 dark:text-slate-400 text-xs">
                                                {log.last_active ? new Date(log.last_active).toLocaleString() : "Never"}
                                            </td>
                                            <td className="p-4 text-xs font-mono text-slate-500">{log.workspace}</td>
                                            <td className="p-4 text-right">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                    log.is_active ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20" : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                                                }`}>
                                                    {log.is_active ? "Active" : "Idle"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 4. REPORTS & PAYMENTS */}
                {activeTab === "payments" && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Payment Auditing & GST Breakdown</h3>
                                <p className="text-xs text-slate-500">GST billing reports for ₹5,000 subscription packages (+18% GST = ₹5,900.00 total).</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={printGSTReport}
                                    className="px-4 py-2 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-xs font-bold text-slate-800 dark:text-slate-200 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center gap-2 shadow-sm"
                                >
                                    <Printer className="w-3.5 h-3.5" />
                                    Print GST Report
                                </button>
                            </div>
                        </div>

                        {/* Conversion pipeline visualizer */}
                        <div className="p-5 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-sm">
                            <h4 className="font-bold text-slate-500 dark:text-slate-300 text-xs uppercase tracking-wider">Lead Conversion Funnel Pipeline</h4>
                            <div className="space-y-3">
                                {reportsData.funnel.map((f, i) => {
                                    const maxVal = reportsData.funnel[0]?.count || 1;
                                    const pct = Math.round((f.count / maxVal) * 100);
                                    return (
                                        <div key={i} className="space-y-1">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-600 dark:text-slate-400 font-medium">{f.stage}</span>
                                                <span className="text-slate-900 dark:text-white font-semibold">{f.count} ({pct}%)</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-100 dark:bg-slate-950 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                                                    style={{ width: `${pct}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Transactions sheet */}
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800/80">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="p-4">Transaction / Order ID</th>
                                        <th className="p-4">Workspace</th>
                                        <th className="p-4">Base Amount</th>
                                        <th className="p-4">CGST (9%)</th>
                                        <th className="p-4">SGST (9%)</th>
                                        <th className="p-4">Total Paid</th>
                                        <th className="p-4">Invoice Date</th>
                                        <th className="p-4 text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {reportsData.payments.map((p) => (
                                        <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition">
                                            <td className="p-4 text-xs font-mono text-slate-500 dark:text-slate-400">{p.id}</td>
                                            <td className="p-4 text-slate-800 dark:text-slate-300 font-semibold">{p.workspace}</td>
                                            <td className="p-4 text-slate-700 dark:text-slate-200">₹{p.base_price.toFixed(2)}</td>
                                            <td className="p-4 text-slate-500 dark:text-slate-400">₹{p.cgst.toFixed(2)}</td>
                                            <td className="p-4 text-slate-500 dark:text-slate-400">₹{p.sgst.toFixed(2)}</td>
                                            <td className="p-4 text-emerald-600 dark:text-emerald-400 font-bold">₹{p.amount.toFixed(2)}</td>
                                            <td className="p-4 text-slate-500 text-xs">
                                                {p.created_at ? new Date(p.created_at).toLocaleDateString() : "N/A"}
                                            </td>
                                            <td className="p-4 text-right">
                                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                                    {p.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 5. SECURITY AUDIT */}
                {activeTab === "security" && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Security Access Audit Logs</h3>
                        <p className="text-xs text-slate-500">Lockouts and failed authentication logs gathered across platform instances.</p>
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800/80">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="p-4">Workspace</th>
                                        <th className="p-4">Identifier (IP/Email)</th>
                                        <th className="p-4">Failed Counts</th>
                                        <th className="p-4">Last Violation Attempt</th>
                                        <th className="p-4 text-right">Lockout Active</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {securityData.map((s, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition">
                                            <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{s.workspace}</td>
                                            <td className="p-4 text-xs font-mono text-slate-500 dark:text-slate-400">{s.identifier}</td>
                                            <td className="p-4 font-semibold text-amber-600 dark:text-amber-500">{s.count} failed attempts</td>
                                            <td className="p-4 text-slate-650 dark:text-slate-400 text-xs">
                                                {s.last_attempt ? new Date(s.last_attempt).toLocaleString() : "N/A"}
                                            </td>
                                            <td className="p-4 text-right">
                                                {s.locked_until ? (
                                                    <span className="px-2 py-0.5 bg-red-500/10 text-red-650 dark:text-red-400 border border-red-500/20 rounded text-[10px] font-bold uppercase">
                                                        Locked until {new Date(s.locked_until).toLocaleTimeString()}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 text-xs">Clear</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 6. LOCATION ANALYTICS */}
                {activeTab === "location" && (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Geographic Location Analytics</h3>
                            <p className="text-xs text-slate-500">Indian State distribution of demos, signups, and trial bookings.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            
                            {/* Table of states count */}
                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800/80">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                                        <tr>
                                            <th className="p-4">State</th>
                                            <th className="p-4">Signups / Demos count</th>
                                            <th className="p-4">Coordinates (Lat, Long)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {locationData.map((loc, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition">
                                                <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{loc.state}</td>
                                                <td className="p-4 text-emerald-600 dark:text-emerald-400 font-bold">{loc.count} leads</td>
                                                <td className="p-4 text-xs font-mono text-slate-500">
                                                    {loc.coordinates[0]}, {loc.coordinates[1]}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* State list stats graphics */}
                            <div className="p-6 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 flex flex-col justify-center shadow-sm">
                                <h4 className="font-bold text-slate-500 dark:text-slate-300 text-xs uppercase tracking-wider">Demand Map Frequency</h4>
                                <div className="space-y-4">
                                    {locationData.map((loc, idx) => (
                                        <div key={idx} className="space-y-1">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-600 dark:text-slate-400">{loc.state}</span>
                                                <span className="text-slate-950 dark:text-white font-bold">{loc.count} bookings</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-950 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-emerald-500 rounded-full"
                                                    style={{ width: `${Math.min(loc.count * 10, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
